import { VALIDATE_INTAKE_FUNCTION } from './intake-tool-functions';
import { findNextBlock, formatValidationErrors } from './intake-validation-helpers';
import { buildFieldIdMap, conditionLeafClauses, isConditionMet, isFormConditionMet, validateIntakeData } from './transform-schema';
const VALIDATE_TOOL = `\`${VALIDATE_INTAKE_FUNCTION.name}\``;
export const getDynamicIntakeState = (blocks, intakeData, saveOutcome) => {
    const savedBlocks = blocks.filter((block) => isConditionMet(block, intakeData, blocks) && intakeData[block.title] !== undefined);
    const failedBlock = blocks.find((block) => saveOutcome?.failedBlockTitles.includes(block.title) && isConditionMet(block, intakeData, blocks));
    const searchFrom = blocks.findIndex((block) => block.title === saveOutcome?.lastSavedBlockTitle) + 1;
    const nextAfterSave = findNextBlock(blocks.slice(searchFrom), intakeData, { blocks });
    const nextTitle = nextAfterSave ?? validateIntakeData({ blocks }, intakeData)[0]?.blockTitle;
    const nextBlock = failedBlock ?? blocks.find((block) => block.title === nextTitle);
    const firstConditionalIndex = blocks.findIndex((block) => !!block.condition);
    const leadingBlocks = firstConditionalIndex === -1 ? blocks : blocks.slice(0, firstConditionalIndex);
    const exposedBlocks = blocks.filter((block) => leadingBlocks.includes(block) || savedBlocks.includes(block) || block === nextBlock);
    return { nextBlock, exposedBlocks };
};
export const buildDynamicIntakePrompt = (intakeComplete, laterToolNames) => {
    if (intakeComplete)
        return `<DYNAMIC_INTAKE>\nAll blocks are saved. The steps after the form are now available: start by calling ${VALIDATE_TOOL}.\n</DYNAMIC_INTAKE>`;
    const tools = laterToolNames.map((name) => `\`${name}\``);
    const toolList = tools.length > 1 ? `${tools.slice(0, -1).join(', ')} and ${tools.at(-1)}` : tools.join('');
    return [
        '<DYNAMIC_INTAKE>',
        'The form above lists only the blocks you can reach so far. More blocks are added as you save.',
        'After each save, continue with the block the save reply names as Next.',
        `The steps after the form (validation, confirmation and submission) are not available yet, so ${toolList} ${tools.length > 1 ? 'are' : 'is'} not in your tool list. Where other instructions mention them, they refer to a later part of the call.`,
        "Only call tools that are in your tool list. Never call a different tool in place of one you don't have.",
        `When a save reply says all blocks are complete, ${VALIDATE_TOOL} is added to your tool list right after that save. Call it as soon as you have it, without telling the caller anything first.`,
        '</DYNAMIC_INTAKE>'
    ].join('\n');
};
export const formatDynamicSaveReply = (blocks, savedBlockTitle, nextBlockTitle) => nextBlockTitle
    ? formatValidationErrors([], blocks, { savedBlocks: [savedBlockTitle], nextBlockTitle })
    : `${savedBlockTitle} saved. All blocks are complete. Next: call ${VALIDATE_TOOL}.`;
// The options each exposed field offers given the answers so far. A rule reading a question the agent can still answer
// now can't be decided yet: that option stays, with its rule. Such a question sits in an exposed block not saved yet, or
// in the field's own block, whose re-save can correct it along with the field.
export const withOfferedOptions = (exposedBlocks, intakeData, blocks) => {
    const unsavedFieldIds = buildFieldIdMap(exposedBlocks.filter((block) => intakeData[block.title] === undefined));
    const readsOpenField = (rule, openFieldIds) => conditionLeafClauses(rule).some((leaf) => (!!leaf.fieldId && openFieldIds.has(leaf.fieldId)) ||
        ('expectedValueFieldId' in leaf &&
            !!leaf.expectedValueFieldId &&
            openFieldIds.has(leaf.expectedValueFieldId)));
    // What the answers so far decide: an "and" with a failed part fails, an "or" with a passed part passes, undefined
    // while the open questions can still go either way
    const decided = (rule, openFieldIds) => {
        if ('allOf' in rule) {
            const parts = rule.allOf.map((part) => decided(part, openFieldIds));
            if (parts.includes(false))
                return false;
            return parts.every((part) => part === true) ? true : undefined;
        }
        if ('anyOf' in rule) {
            const parts = rule.anyOf.map((part) => decided(part, openFieldIds));
            if (parts.includes(true))
                return true;
            return parts.every((part) => part === false) ? false : undefined;
        }
        return readsOpenField(rule, openFieldIds) ? undefined : isFormConditionMet(rule, intakeData, blocks);
    };
    const narrow = (openFieldIds) => ({ enumWhen, ...field }) => {
        if (!enumWhen)
            return field;
        const verdicts = Object.fromEntries(Object.entries(enumWhen).map(([option, rule]) => [option, decided(rule, openFieldIds)]));
        const open = Object.fromEntries(Object.entries(enumWhen).filter(([option]) => verdicts[option] === undefined));
        return {
            ...field,
            enum: (field.enum ?? []).filter((option) => verdicts[option] !== false),
            ...(Object.keys(open).length > 0 && { enumWhen: open })
        };
    };
    return exposedBlocks.map((block) => {
        if (!block.fields.some((f) => f.enumWhen))
            return block;
        const openFieldIds = new Map([...unsavedFieldIds, ...buildFieldIdMap([block])]);
        return { ...block, fields: block.fields.map(narrow(openFieldIds)) };
    });
};
//# sourceMappingURL=dynamic-intake-state.js.map